import type { RateIncentiveItem, RatePaidLeavePayload, RateRow, RateTaxItem } from "@/lib/rate-api";
import { parseClockLikeToSecondsFromMidnight, parseToYmd } from "@/lib/format";

export type AdditionalHoursDetail = {
  status?: string;
  start_hours?: string;
  end_hours?: string;
  total_hours?: string;
  date_add?: string;
  project?: string;
  reason?: string;
};

export type AdditionalHoursPayload = {
  name_employee?: string;
  total_hours_additing?: string;
  total_add?: number;
  details_additing?: AdditionalHoursDetail[];
};

export type DetailsProject = {
  name_employee?: string;
  projects: any[];
  totals?: any;
  report?: {
    title?: string;
    sheetName?: string | null;
    dateRange?: { raw?: string; from?: string; to?: string };
    timezone?: string;
  } | null;
};

export type ReimburseDetailRow = {
  date_add?: string;
  description?: string;
  reimburse?: number;
  proof?: string;
};

export type ReimburseRatePayload = {
  name_employee?: string;
  total_reimburse?: number;
  total_count?: number;
  details_reimburse?: ReimburseDetailRow[];
};

export type RatesSummary = {
  totalEmployees: number;
  loggedTime: string;
  activeTime: string;
  activityPct: number;
  cost: number;
  additionalEarn: number;
  reimburseTotal: number;
  incentiveTotal: number;
  taxTotal: number;
  paidLeaveDays: number;
  paidLeaveEarn: number;
  grandEarn: number;
};

export function hmsToSeconds(s: string): number {
  const parts = String(s ?? "").split(":").map((x) => Number(x));
  if (parts.length !== 3) return 0;
  const [hh, mm, ss] = parts;
  if (![hh, mm, ss].every((n) => Number.isFinite(n) && n >= 0)) return 0;
  return hh * 3600 + mm * 60 + ss;
}

export function secondsToHms(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function hhDotMmToSeconds(raw: unknown): number {
  const s = String(raw ?? "").trim();
  if (!s) return 0;
  const m = s.match(/^(\d{1,3})\.(\d{1,2})$/);
  if (!m) return 0;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (![hh, mm].every((n) => Number.isFinite(n) && n >= 0)) return 0;
  if (mm >= 60) return 0;
  return hh * 3600 + mm * 60;
}

export function safeAdditionalHoursPayload(raw: unknown): AdditionalHoursPayload | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    try {
      return safeAdditionalHoursPayload(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    name_employee: typeof o.name_employee === "string" ? o.name_employee : undefined,
    total_hours_additing: typeof o.total_hours_additing === "string" ? o.total_hours_additing : undefined,
    total_add: typeof o.total_add === "number" ? o.total_add : undefined,
    details_additing: Array.isArray(o.details_additing) ? (o.details_additing as AdditionalHoursDetail[]) : undefined,
  };
}

export function safeDetailsProject(details: unknown): DetailsProject {
  if (!details || typeof details !== "object") return { projects: [] };
  const d = details as any;
  return {
    name_employee: typeof d.name_employee === "string" ? d.name_employee : undefined,
    projects: Array.isArray(d.projects) ? d.projects : [],
    totals: d.totals,
    report: d.report ?? null,
  };
}

export function safeReimbursePayload(raw: unknown): ReimburseRatePayload | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    try {
      return safeReimbursePayload(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const details = Array.isArray(o.details_reimburse) ? (o.details_reimburse as ReimburseDetailRow[]) : undefined;
  return {
    name_employee: typeof o.name_employee === "string" ? o.name_employee : undefined,
    total_reimburse: typeof o.total_reimburse === "number" && Number.isFinite(o.total_reimburse) ? o.total_reimburse : undefined,
    total_count: typeof o.total_count === "number" && Number.isFinite(o.total_count) ? o.total_count : undefined,
    details_reimburse: details,
  };
}

export function parseRateIncentiveItems(raw: unknown): RateIncentiveItem[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw === "string") {
    try {
      return parseRateIncentiveItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.items)) return [];
  const out: RateIncentiveItem[] = [];
  for (const it of o.items as Record<string, unknown>[]) {
    if (!it || typeof it !== "object") continue;
    const jenis = (it.jenis ?? it.jenis_insentif) as string | undefined;
    if (jenis !== "jabatan" && jenis !== "wfo") continue;
    const id = String(it.id ?? "").trim();
    const nominal = Number(it.nominal ?? it.nominal_insentif ?? 0);
    if (!id || !Number.isFinite(nominal) || nominal < 0) continue;
    const cat = it.catatan ?? it.catatan_insentif;
    out.push({
      id,
      jenis: jenis as RateIncentiveItem["jenis"],
      nominal,
      catatan: typeof cat === "string" && cat.trim() ? cat.trim() : undefined,
    });
  }
  return out;
}

export function incentiveTotalFromRate(raw: unknown): number {
  const items = parseRateIncentiveItems(raw);
  if (items.length > 0) return items.reduce((a, i) => a + i.nominal, 0);
  if (raw && typeof raw === "object") {
    const t = (raw as Record<string, unknown>).total_incentive;
    if (typeof t === "number" && Number.isFinite(t)) return t;
  }
  return 0;
}

export function parseRateTaxItems(raw: unknown): RateTaxItem[] {
  if (raw === null || raw === undefined) return [];
  if (typeof raw === "string") {
    try {
      return parseRateTaxItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }
  if (typeof raw !== "object") return [];
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.items)) return [];
  const out: RateTaxItem[] = [];
  for (const it of o.items as Record<string, unknown>[]) {
    if (!it || typeof it !== "object") continue;
    const jenis = (it.jenis ?? it.jenis_pajak) as string | undefined;
    if (jenis !== "pph21" && jenis !== "pph23" && jenis !== "lainnya") continue;
    const id = String(it.id ?? "").trim();
    const nominal = Number(it.nominal ?? it.nominal_pajak ?? 0);
    if (!id || !Number.isFinite(nominal) || nominal < 0) continue;
    const cat = it.catatan ?? it.catatan_pajak;
    out.push({
      id,
      jenis: jenis as RateTaxItem["jenis"],
      nominal,
      catatan: typeof cat === "string" && cat.trim() ? cat.trim() : undefined,
    });
  }
  return out;
}

export function taxTotalFromRate(raw: unknown): number {
  const items = parseRateTaxItems(raw);
  if (items.length > 0) return items.reduce((a, i) => a + i.nominal, 0);
  if (raw && typeof raw === "object") {
    const t = (raw as Record<string, unknown>).total_tax;
    if (typeof t === "number" && Number.isFinite(t)) return t;
  }
  return 0;
}

export function safePaidLeavePayload(raw: unknown): RatePaidLeavePayload | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") {
    try {
      return safePaidLeavePayload(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const dates = Array.isArray(o.dates) ? (o.dates as unknown[]).map((d) => String(d)).filter(Boolean) : [];
  if (dates.length === 0) return null;
  const hours_per_day = Number(o.hours_per_day ?? 8);
  const proof = o.proof === "tidak terlampir" ? "tidak terlampir" : "terlampir";
  const ymdOk = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const work_required_dates = Array.isArray(o.work_required_dates)
    ? (o.work_required_dates as unknown[])
        .map((d) => String(d))
        .filter((d) => ymdOk(d) && dates.includes(d))
    : [];
  return {
    name_employee: typeof o.name_employee === "string" ? o.name_employee : "",
    total_days: Number(o.total_days ?? dates.length) || dates.length,
    total_hours: Number(o.total_hours ?? dates.length * hours_per_day) || dates.length * hours_per_day,
    hours_per_day: Number.isFinite(hours_per_day) && hours_per_day > 0 ? hours_per_day : 8,
    reason: typeof o.reason === "string" ? o.reason : "",
    proof,
    dates,
    work_required_dates,
  };
}

/**
 * Hari cuti berbayar tanpa wajib kerja pada tanggal tersebut: jam dalam rentang setempat
 * [08:00:00, 16:01:00) tidak diperhitungkan (digantikan cuti). Luar rentang itu tetap dihitung.
 */
export function paidLeaveSuppressesWorkedTimeOnYmd(pl: RatePaidLeavePayload | null, ymd: string | null | undefined): boolean {
  if (!pl || !ymd) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  if (!pl.dates.includes(ymd)) return false;
  const wr = pl.work_required_dates ?? [];
  return !wr.includes(ymd);
}

/** Pada hari cuti tanpa wajib kerja: rentang setengah-buka [08:00:00, 16:01:00) tidak boleh dihitung. */
const CUTI_DAY_FORBIDDEN_START_SEC = 8 * 3600;
const CUTI_DAY_FORBIDDEN_END_EXCLUSIVE_SEC = 16 * 3600 + 60;

function overlapHalfOpen(a0: number, a1Exclusive: number, b0: number, b1Exclusive: number): number {
  return Math.max(0, Math.min(a1Exclusive, b1Exclusive) - Math.max(a0, b0));
}

export type CutiClockWallMeta = {
  wallSec: number;
  forbiddenOverlapSec: number;
  countedWallSec: number;
  voidRatio: number;
  /** Segmen yang masih dihitung, contoh "07:33:44–07:59:59" atau dua segmen dipisah " · ". */
  countedSegmentsLabel: string;
};

function buildCountedSegmentsLabel(startSec: number, endExclusiveSec: number): string {
  const F0 = CUTI_DAY_FORBIDDEN_START_SEC;
  const F1 = CUTI_DAY_FORBIDDEN_END_EXCLUSIVE_SEC;
  const parts: string[] = [];
  if (startSec < F0) {
    const e = Math.min(endExclusiveSec, F0);
    if (e > startSec) {
      const lastIncl = e - 1;
      parts.push(`${secondsToHms(startSec)}–${secondsToHms(lastIncl)}`);
    }
  }
  if (endExclusiveSec > F1) {
    const s = Math.max(startSec, F1);
    if (endExclusiveSec > s) {
      const lastIncl = endExclusiveSec - 1;
      parts.push(`${secondsToHms(s)}–${secondsToHms(lastIncl)}`);
    }
  }
  return parts.join(" · ");
}

/**
 * Irisan sesi dengan rentang terlarang cuti.
 * Waktu selesai dianggap inklusif detik terakhir kerja → dipetakan ke [start, endExclusive).
 */
export function cutiClockHangusMetaForTimes(timeStart: unknown, timeEnd: unknown): CutiClockWallMeta | null {
  const startSec = parseClockLikeToSecondsFromMidnight(String(timeStart ?? ""));
  const endLastInclusive = parseClockLikeToSecondsFromMidnight(String(timeEnd ?? ""));
  if (startSec == null || endLastInclusive == null) return null;
  if (endLastInclusive < startSec) return null;

  const endExclusive = endLastInclusive + 1;
  const wallSec = endExclusive - startSec;
  if (wallSec <= 0) return null;

  const forbiddenOverlapSec = overlapHalfOpen(
    startSec,
    endExclusive,
    CUTI_DAY_FORBIDDEN_START_SEC,
    CUTI_DAY_FORBIDDEN_END_EXCLUSIVE_SEC,
  );
  const countedWallSec = Math.max(0, wallSec - forbiddenOverlapSec);
  const voidRatio = Math.min(1, Math.max(0, forbiddenOverlapSec / wallSec));
  const countedSegmentsLabel = buildCountedSegmentsLabel(startSec, endExclusive);

  return {
    wallSec,
    forbiddenOverlapSec,
    countedWallSec,
    voidRatio,
    countedSegmentsLabel,
  };
}

export type CutiHangusSessionEffect = {
  applicable: boolean;
  voidRatio: number;
  meta: CutiClockWallMeta | null;
};

/** Potongan hangus untuk satu sesi timesheet vs cuti (tanggal mulai) + wajib kerja. */
export function cutiHangusEffectForSession(
  pl: RatePaidLeavePayload | null,
  s: { dateStart?: unknown; dateEnd?: unknown; timeStart?: unknown; timeEnd?: unknown },
): CutiHangusSessionEffect {
  const ymd = parseToYmd(String(s?.dateStart ?? ""));
  if (!ymd || !paidLeaveSuppressesWorkedTimeOnYmd(pl, ymd)) {
    return { applicable: false, voidRatio: 0, meta: null };
  }
  const endYmdRaw = s?.dateEnd != null && String(s.dateEnd).trim() ? parseToYmd(String(s.dateEnd)) : null;
  if (endYmdRaw && endYmdRaw !== ymd) {
    return { applicable: true, voidRatio: 1, meta: null };
  }
  const meta = cutiClockHangusMetaForTimes(s.timeStart, s.timeEnd);
  if (!meta) return { applicable: true, voidRatio: 1, meta: null };
  return { applicable: true, voidRatio: meta.voidRatio, meta };
}

export type CutiHangusAdditionalEffect = {
  applicable: boolean;
  voidRatio: number;
  meta: CutiClockWallMeta | null;
};

export function cutiHangusEffectForAdditionalRow(
  pl: RatePaidLeavePayload | null,
  row: AdditionalHoursDetail,
  rowSecondsFallback: number,
): CutiHangusAdditionalEffect {
  const ymd = parseToYmd(row?.date_add ?? null);
  if (!ymd || !paidLeaveSuppressesWorkedTimeOnYmd(pl, ymd)) {
    return { applicable: false, voidRatio: 0, meta: null };
  }
  const meta = cutiClockHangusMetaForTimes(row?.start_hours, row?.end_hours);
  if (!meta) {
    const vr = rowSecondsFallback > 0 ? 1 : 0;
    return { applicable: true, voidRatio: vr, meta: null };
  }
  return { applicable: true, voidRatio: meta.voidRatio, meta };
}

function sumCutiSuppressedAdditionalSeconds(add: AdditionalHoursPayload | null, pl: RatePaidLeavePayload | null): number {
  if (!add?.details_additing?.length) return 0;
  let sec = 0;
  for (const it of add.details_additing) {
    const rowSec = hhDotMmToSeconds(it?.total_hours);
    const eff = cutiHangusEffectForAdditionalRow(pl, it, rowSec);
    if (!eff.applicable || eff.voidRatio <= 0) continue;
    sec += rowSec * eff.voidRatio;
  }
  return sec;
}

export type CutiAdjustedRateTotals = {
  effectiveBaseEarn: number | null;
  adjustedLoggedSeconds: number;
  adjustedActiveSeconds: number;
  adjustedAddSeconds: number;
  adjustedAddEarn: number | null;
  suppressedSessionEarnIdr: number;
  suppressedAddSeconds: number;
  adjustedActivityPct: number | null;
  hasSuppression: boolean;
};

/** Penyesuaian biaya/jam terhadap cuti: pada hari cuti tanpa wajib kerja, bagian sesi/jam tambahan dalam rentang lokal [08:00, 16:01) tidak dihitung (proporsional). */
export function computeCutiAdjustedTotalsForRateRow(r: RateRow): CutiAdjustedRateTotals {
  const pl = safePaidLeavePayload(r.paid_leave ?? null);
  const details = safeDetailsProject(r.details_project);
  const totals = details.totals || {};
  const baseSeconds = hmsToSeconds(String(totals.loggedTime ?? r.total_hours ?? "0:00:00"));
  const activeSeconds = hmsToSeconds(String(totals.activeTime ?? "0:00:00"));

  let supEarn = 0;
  let supLog = 0;
  let supAct = 0;
  for (const p of details.projects ?? []) {
    const sessions = Array.isArray((p as any).sessions) ? ((p as any).sessions as any[]) : [];
    for (const s of sessions) {
      const eff = cutiHangusEffectForSession(pl, s);
      if (!eff.applicable || eff.voidRatio <= 0) continue;
      const vr = eff.voidRatio;
      const c = typeof s?.cost === "number" && Number.isFinite(s.cost) ? s.cost : 0;
      supEarn += c * 1000 * vr;
      const ls =
        typeof s?.loggedSeconds === "number" && Number.isFinite(s.loggedSeconds)
          ? s.loggedSeconds
          : hmsToSeconds(String(s?.loggedTime ?? "0:00:00"));
      const activityAs =
        typeof s?.activeSeconds === "number" && Number.isFinite(s.activeSeconds)
          ? s.activeSeconds
          : hmsToSeconds(String(s?.activeTime ?? "0:00:00"));
      supLog += Math.max(0, ls) * vr;
      supAct += Math.max(0, activityAs) * vr;
    }
  }

  const rawEarn = typeof r.total_earn === "number" && Number.isFinite(r.total_earn) ? r.total_earn : null;
  const cappedEarnDeduction = rawEarn == null ? 0 : Math.min(supEarn, rawEarn);
  const effectiveBaseEarn = rawEarn == null ? null : Math.max(0, rawEarn - cappedEarnDeduction);

  const adjLog = Math.max(0, baseSeconds - supLog);
  const adjAct = Math.max(0, activeSeconds - supAct);
  const adjustedActivityPct = adjLog > 0 ? Math.round((adjAct / adjLog) * 100) : null;

  const add = safeAdditionalHoursPayload(r.additional_hours ?? null);
  const addSecondsFull =
    add?.details_additing && add.details_additing.length > 0
      ? add.details_additing.reduce((acc, it) => acc + hhDotMmToSeconds(it?.total_hours), 0)
      : hhDotMmToSeconds(add?.total_hours_additing);
  const supAddSec = sumCutiSuppressedAdditionalSeconds(add, pl);
  const adjustedAddSeconds = Math.max(0, addSecondsFull - supAddSec);
  const adjustedAddEarn =
    typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours) ? (adjustedAddSeconds / 3600) * r.rate_hours : null;

  const hasSuppression = supEarn > 0 || supAddSec > 0;

  return {
    effectiveBaseEarn,
    adjustedLoggedSeconds: adjLog,
    adjustedActiveSeconds: adjAct,
    adjustedAddSeconds,
    adjustedAddEarn,
    suppressedSessionEarnIdr: supEarn,
    suppressedAddSeconds: supAddSec,
    adjustedActivityPct,
    hasSuppression,
  };
}

/** Agregasi yang sama dengan kartu ringkasan di halaman detail periode. */
export function summarizeRates(rates: RateRow[]): RatesSummary {
  let logged = 0;
  let active = 0;
  let cost = 0;
  let additionalEarn = 0;
  let reimburseTotal = 0;
  let incentiveTotal = 0;
  let taxTotal = 0;
  let paidLeaveEarn = 0;
  let paidLeaveDays = 0;
  for (const r of rates) {
    const adj = computeCutiAdjustedTotalsForRateRow(r);
    logged += adj.adjustedLoggedSeconds;
    active += adj.adjustedActiveSeconds;
    cost += adj.effectiveBaseEarn ?? 0;

    if (typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours)) {
      additionalEarn += adj.adjustedAddEarn ?? 0;
    }

    const reimb = safeReimbursePayload(r.reimburse ?? null);
    if (reimb) {
      if (typeof reimb.total_reimburse === "number" && Number.isFinite(reimb.total_reimburse)) {
        reimburseTotal += reimb.total_reimburse;
      } else {
        reimburseTotal += (reimb.details_reimburse ?? []).reduce(
          (acc, d) => acc + (typeof d?.reimburse === "number" && Number.isFinite(d.reimburse) ? d.reimburse : 0),
          0,
        );
      }
    }

    incentiveTotal += incentiveTotalFromRate(r.incentive);
    taxTotal += taxTotalFromRate(r.tax);

    const pl = safePaidLeavePayload(r.paid_leave ?? null);
    if (pl) {
      paidLeaveDays += pl.total_days;
      if (typeof r.rate_hours === "number" && Number.isFinite(r.rate_hours)) {
        paidLeaveEarn += pl.total_hours * r.rate_hours;
      }
    }
  }
  const activityPct = logged > 0 ? Math.round((active / logged) * 100) : 0;
  return {
    totalEmployees: rates.length,
    loggedTime: secondsToHms(logged),
    activeTime: secondsToHms(active),
    activityPct,
    cost,
    additionalEarn,
    reimburseTotal,
    incentiveTotal,
    taxTotal,
    paidLeaveDays,
    paidLeaveEarn,
    grandEarn: cost + additionalEarn + reimburseTotal + incentiveTotal + paidLeaveEarn - taxTotal,
  };
}
